import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatDate } from '../../lib/utils';
import { useFormatting } from '../../hooks/useFormatting';
import { DEMO_TRANSACTIONS } from '../../data/demo-data';
import { Tag, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

interface CategorizationModalProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORIES = [
  'Revenue',
  'Payroll',
  'Rent',
  'Utilities',
  'Supplies',
  'Equipment',
  'Marketing',
  'Professional Fees',
  'Insurance',
  'Taxes',
  'Travel',
  'Office Expenses',
  'Software & Subscriptions',
  'Other',
];

export const CategorizationModal: React.FC<CategorizationModalProps> = ({
  open,
  onClose,
}) => {
  const { formatCurrency } = useFormatting();
  
  const uncategorizedTransactions = DEMO_TRANSACTIONS.filter(
    (txn) => txn.category === 'Uncategorised'
  ).slice(0, 5);

  const [categories, setCategories] = useState<Record<string, string>>({});
  const [categorizedIds, setCategorizedIds] = useState<Set<string>>(new Set());

  const handleCategoryChange = (transactionId: string, category: string) => {
    setCategories((prev) => ({
      ...prev,
      [transactionId]: category,
    }));
  };

  const handleCategorize = (transactionId: string) => {
    const category = categories[transactionId];
    if (!category) {
      toast.error('Please select a category');
      return;
    }

    setCategorizedIds((prev) => new Set(prev).add(transactionId));
    toast.success(`Transaction categorized as ${category}`);
  };

  const handleCategorizeAll = () => {
    const allCategorized = uncategorizedTransactions.every(
      (txn) => categories[txn.id] && categorizedIds.has(txn.id)
    );

    if (!allCategorized) {
      const uncategorized = uncategorizedTransactions.filter(
        (txn) => !categories[txn.id]
      );
      if (uncategorized.length > 0) {
        toast.error('Please assign categories to all transactions');
        return;
      }

      uncategorizedTransactions.forEach((txn) => {
        if (!categorizedIds.has(txn.id)) {
          setCategorizedIds((prev) => new Set(prev).add(txn.id));
        }
      });
      toast.success(`${uncategorizedTransactions.length} transactions categorized`);
    }

    setTimeout(() => {
      onClose();
    }, 1000);
  };

  const allCategorized = uncategorizedTransactions.every(
    (txn) => categorizedIds.has(txn.id)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Categorise Transactions
          </DialogTitle>
          <DialogDescription>
            Assign categories to uncategorized transactions to improve your cash flow
            insights
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {uncategorizedTransactions.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
              <p className="text-muted-foreground">
                All transactions have been categorized
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uncategorizedTransactions.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell className="font-medium">
                      {formatDate(txn.date)}
                    </TableCell>
                    <TableCell>{txn.description}</TableCell>
                    <TableCell
                      className={`text-right font-semibold ${
                        txn.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {txn.amount > 0 ? '+' : ''}
                      {formatCurrency(Math.abs(txn.amount))}
                    </TableCell>
                    <TableCell>
                      {categorizedIds.has(txn.id) ? (
                        <Badge variant="secondary" className="bg-green-100 text-green-700">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          {categories[txn.id]}
                        </Badge>
                      ) : (
                        <Select
                          value={categories[txn.id] || ''}
                          onValueChange={(value) =>
                            handleCategoryChange(txn.id, value)
                          }
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map((category) => (
                              <SelectItem key={category} value={category}>
                                {category}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      {!categorizedIds.has(txn.id) && (
                        <Button
                          size="sm"
                          onClick={() => handleCategorize(txn.id)}
                          disabled={!categories[txn.id]}
                        >
                          Categorise
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {uncategorizedTransactions.length > 0 && (
            <Button onClick={handleCategorizeAll} disabled={allCategorized}>
              {allCategorized ? 'All Categorized' : 'Save All Categories'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
